/**
 * Activos fijos — Finance 360 (sprint 8.5).
 *
 * ── LAS TRES COSAS QUE ESTA PANTALLA TIENE QUE DEJAR CLARAS ─────────────────
 *
 * **Lo que no está en marcha no deprecia.** No es un estado decorativo: es la
 * diferencia entre cargarle a la empresa el gasto de algo que produce y el de
 * algo que está en una bodega esperando instalación. Por eso un activo en curso
 * se ve distinto y lo primero que ofrece es ponerlo en marcha.
 *
 * **El libro que se mira cambia los números.** El contable y el fiscal no
 * coinciden —en Costa Rica casi nunca— y mostrarlos mezclados no es un detalle
 * de presentación: es un calendario que no suma. El selector de libro está
 * arriba del calendario, no escondido.
 *
 * **Lo contabilizado y lo arrastrado no se recalculan.** Una cuota contabilizada
 * generó un asiento; una arrastrada viene del sistema anterior y su gasto ya
 * está en el mayor. Las dos se muestran con su marca, porque el usuario que
 * cambia la vida útil tiene que ver qué parte del calendario NO se va a mover.
 */

import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  Package, Plus, Upload, ArrowLeft, PlayCircle, RefreshCw, TrendingDown,
  X, Lock, History, AlertTriangle
} from 'lucide-react';
import { contabilidadApi } from '../../utils/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import ImportarActivos from '../../components/activos/ImportarActivos';

const ESTADOS = {
  en_curso: { rotulo: 'En curso', clase: 'bg-amber-100 text-amber-800' },
  activo: { rotulo: 'Activo', clase: 'bg-emerald-100 text-emerald-800' },
  dado_de_baja: { rotulo: 'Dado de baja', clase: 'bg-gray-200 text-gray-700' }
};

const CUOTAS = {
  proyectado: { rotulo: 'Proyectada', clase: 'text-gray-600' },
  contabilizado: { rotulo: 'Contabilizada', clase: 'text-emerald-700' },
  arrastrado: { rotulo: 'Del sistema anterior', clase: 'text-blue-700' },
  anulado: { rotulo: 'Anulada', clase: 'text-gray-400 line-through' }
};

const plata = (n) => Number(n || 0).toLocaleString('en-US',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** El mensaje del servidor es el que explica la regla; no se reemplaza por uno genérico. */
const avisar = (e, siNo) => toast.error(e?.response?.data?.error || e?.message || siNo);

function ModalActivo ({ clases, onCerrar, onGuardado }) {
  const [datos, setDatos] = useState({
    codigo: '', nombre: '', claseId: '', fechaAdquisicion: '',
    fechaPuestaEnMarcha: '', valorAdquisicion: '', descripcion: ''
  });
  const [guardando, setGuardando] = useState(false);

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await contabilidadApi.createActivo({
        ...datos,
        claseId: Number(datos.claseId),
        valorAdquisicion: Number(datos.valorAdquisicion),
        fechaPuestaEnMarcha: datos.fechaPuestaEnMarcha || null
      });
      toast.success('Activo creado');
      onGuardado();
    } catch (err) {
      avisar(err, 'No se pudo crear');
    } finally {
      setGuardando(false);
    }
  };

  const campo = (clave, rotulo, extra = {}) => (
    <div>
      <label htmlFor={`a-${clave}`} className="block text-sm font-medium text-gray-700 mb-1">
        {rotulo} {extra.required && <span className="text-red-600">*</span>}
      </label>
      <input
        id={`a-${clave}`}
        value={datos[clave]}
        onChange={(e) => setDatos({ ...datos, [clave]: e.target.value })}
        className="w-full border rounded-lg px-3 py-2 text-sm"
        {...extra}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <form onSubmit={guardar} className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-semibold text-lg">Nuevo activo</h3>
          <button type="button" onClick={onCerrar} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            {campo('codigo', 'Código', { required: true, maxLength: 40, placeholder: 'MAQ-001' })}
            <div>
              <label htmlFor="a-clase" className="block text-sm font-medium text-gray-700 mb-1">
                Clase <span className="text-red-600">*</span>
              </label>
              <select
                id="a-clase"
                value={datos.claseId}
                onChange={(e) => setDatos({ ...datos, claseId: e.target.value })}
                required
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Elija una…</option>
                {clases.map(c => (
                  <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
                ))}
              </select>
              {/* La clase no es una etiqueta: determina las cuentas contables y
                  la vida útil con la que se arma el calendario. */}
              <p className="text-xs text-gray-500 mt-1">
                Define las cuentas contables y la vida útil por omisión.
              </p>
            </div>
          </div>

          {campo('nombre', 'Nombre', { required: true, placeholder: 'Tractor John Deere 5090E' })}

          <div className="grid grid-cols-2 gap-4">
            {campo('fechaAdquisicion', 'Fecha de adquisición', { required: true, type: 'date' })}
            {campo('valorAdquisicion', 'Valor de adquisición', {
              required: true, type: 'number', step: '0.01', min: '0.01'
            })}
          </div>

          <div>
            {campo('fechaPuestaEnMarcha', 'Fecha de puesta en marcha', { type: 'date' })}
            {/* Deliberadamente opcional: una obra en curso es un activo
                legítimo, y exigir la fecha obligaría a inventarla — que es
                exactamente cómo se pierde el dato que decide desde cuándo se
                deprecia. */}
            <p className="text-xs text-gray-500 mt-1">
              Déjela vacía si todavía no está instalado: entra como obra en curso y no
              deprecia hasta que se ponga en marcha.
            </p>
          </div>

          {campo('descripcion', 'Observaciones')}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t bg-gray-50 rounded-b-xl">
          <button type="button" onClick={onCerrar} className="px-4 py-2 text-sm border rounded-lg hover:bg-white">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={guardando}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {guardando ? 'Guardando…' : 'Crear activo'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Ficha ({ id, libros, oficial, onVolver, onCambio }) {
  const [datos, setDatos] = useState(null);
  const [libroId, setLibroId] = useState(oficial?.id || null);
  const [cargando, setCargando] = useState(true);
  const [marcha, setMarcha] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await contabilidadApi.getActivo(id);
      setDatos(r.data);
    } catch (e) {
      avisar(e, 'No se pudo cargar el activo');
    } finally {
      setCargando(false);
    }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  if (cargando || !datos) return <LoadingSpinner />;
  const a = datos.activo;
  const est = ESTADOS[a.estado] || { rotulo: a.estado, clase: 'bg-gray-100' };

  const delLibro = datos.calendario.filter(c => !libroId || c.libroId === libroId);
  const acumulado = delLibro
    .filter(c => c.estado === 'contabilizado' || c.estado === 'arrastrado')
    .reduce((s, c) => s + c.monto, 0);
  const enLibros = a.valorAdquisicion - acumulado;
  const cfg = datos.config.find(c => c.libroId === libroId);

  const ponerEnMarcha = async () => {
    if (!marcha) { toast.error('Indique la fecha'); return; }
    try {
      await contabilidadApi.ponerActivoEnMarcha(a.id, marcha);
      toast.success('Puesto en marcha; se calculó el calendario');
      setMarcha('');
      await cargar(); onCambio?.();
    } catch (e) { avisar(e, 'No se pudo poner en marcha'); }
  };

  const recalcular = async () => {
    try {
      await contabilidadApi.recalcularActivo(a.id);
      toast.success('Calendario recalculado; lo contabilizado no se tocó');
      await cargar();
    } catch (e) { avisar(e, 'No se pudo recalcular'); }
  };

  return (
    <div className="space-y-5">
      <button onClick={onVolver} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
        <ArrowLeft size={16} /> Volver a los activos
      </button>

      <div className="bg-white rounded-xl border p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">{a.codigo}</h2>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${est.clase}`}>
                {est.rotulo}
              </span>
            </div>
            <p className="text-gray-700">{a.nombre}</p>
            {a.descripcion && <p className="text-sm text-gray-500 mt-1">{a.descripcion}</p>}
            {a.origenTipo && a.origenTipo !== 'manual' && (
              <p className="text-xs text-gray-500 mt-2">
                Llegó por {a.origenTipo.replace(/_/g, ' ')}
                {a.origenId ? ` (${a.origenId})` : ''}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {a.estado === 'activo' && (
              <button
                onClick={recalcular}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
              >
                <RefreshCw size={15} /> Recalcular
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 pt-5 border-t">
          {[
            ['Adquisición', a.fechaAdquisicion],
            ['Puesta en marcha', a.fechaPuestaEnMarcha || '—'],
            ['Valor de adquisición', plata(a.valorAdquisicion)],
            ['Valor residual', plata(a.valorResidual)]
          ].map(([r, v]) => (
            <div key={r}>
              <p className="text-xs text-gray-500">{r}</p>
              <p className="text-sm font-medium tabular-nums">{v}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Lo primero que un activo en curso necesita es la fecha que lo arranca.
          Es el dato que el plan llama «el que se pierde siempre». */}
      {a.estado === 'en_curso' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-600 mt-0.5 shrink-0" size={20} />
            <div className="flex-1">
              <p className="font-medium text-amber-900">Todavía no deprecia.</p>
              <p className="text-sm text-amber-800 mt-1 mb-3">
                Está en curso: no genera gasto hasta que se ponga en marcha. La fecha de
                puesta en marcha es la que manda para depreciar, no la de compra.
              </p>
              <div className="flex gap-2 items-end flex-wrap">
                <div>
                  <label htmlFor="en-marcha" className="block text-xs text-amber-900 mb-1">
                    Fecha de puesta en marcha
                  </label>
                  <input
                    id="en-marcha"
                    type="date"
                    value={marcha}
                    min={a.fechaAdquisicion}
                    onChange={(e) => setMarcha(e.target.value)}
                    className="border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <button
                  onClick={ponerEnMarcha}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                >
                  <PlayCircle size={16} /> Poner en marcha
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── El calendario, por libro ─────────────────────────────────────── */}
      {delLibro.length > 0 && (
        <div className="bg-white rounded-xl border">
          <div className="flex items-center justify-between gap-4 px-5 py-4 border-b flex-wrap">
            <h3 className="font-semibold flex items-center gap-2">
              <TrendingDown size={18} className="text-gray-400" /> Depreciación
            </h3>
            <div className="flex items-center gap-3">
              {/* El selector no está escondido a propósito: el contable y el
                  fiscal no coinciden, y verlos mezclados es un calendario que
                  no suma. */}
              <label htmlFor="libro" className="text-sm text-gray-600">Libro</label>
              <select
                id="libro"
                value={libroId || ''}
                onChange={(e) => setLibroId(Number(e.target.value))}
                className="border rounded-lg px-3 py-1.5 text-sm"
              >
                {libros.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.nombre}{l.esOficial ? ' (oficial)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-5 py-4 bg-gray-50 border-b">
            {[
              ['Depreciación acumulada', plata(acumulado)],
              ['Valor en libros', plata(enLibros)],
              ['Vida útil', cfg ? `${cfg.vidaUtilMeses} meses` : '—'],
              ['Cuotas pendientes', String(delLibro.filter(c => c.estado === 'proyectado').length)]
            ].map(([r, v]) => (
              <div key={r}>
                <p className="text-xs text-gray-500">{r}</p>
                <p className="text-sm font-medium tabular-nums">{v}</p>
              </div>
            ))}
          </div>

          {!libros.find(l => l.id === libroId)?.esOficial && (
            <p className="px-5 py-2 text-xs text-blue-800 bg-blue-50 border-b">
              Este libro se calcula y se informa, pero <strong>no genera asientos</strong>:
              solo el oficial publica en la contabilidad.
            </p>
          )}

          <div className="overflow-x-auto max-h-96">
            <table className="min-w-full text-sm">
              <thead className="bg-white sticky top-0 border-b text-gray-600">
                <tr>
                  <th className="px-5 py-2 text-left font-medium">Período</th>
                  <th className="px-5 py-2 text-right font-medium">Cuota</th>
                  <th className="px-5 py-2 text-right font-medium">Acumulado</th>
                  <th className="px-5 py-2 text-left font-medium">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {delLibro.map(c => {
                  const e = CUOTAS[c.estado] || { rotulo: c.estado, clase: '' };
                  const firme = c.estado === 'contabilizado' || c.estado === 'arrastrado';
                  return (
                    <tr key={c.id} className={c.estado === 'anulado' ? 'opacity-50' : ''}>
                      <td className="px-5 py-2 tabular-nums">{c.periodo}</td>
                      <td className="px-5 py-2 text-right tabular-nums">{plata(c.monto)}</td>
                      <td className="px-5 py-2 text-right tabular-nums text-gray-500">
                        {plata(c.acumuladoDespues)}
                      </td>
                      <td className={`px-5 py-2 ${e.clase}`}>
                        <span className="inline-flex items-center gap-1">
                          {/* El candado dice «esto no se va a mover si cambia la
                              vida útil», que es la pregunta real del usuario. */}
                          {firme && <Lock size={12} />}
                          {e.rotulo}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Los movimientos ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border">
        <h3 className="font-semibold flex items-center gap-2 px-5 py-4 border-b">
          <History size={18} className="text-gray-400" /> Historia del activo
        </h3>
        <div className="divide-y">
          {datos.movimientos.map(m => (
            <div key={m.id} className="px-5 py-3 text-sm flex items-start justify-between gap-4">
              <div>
                <span className="font-medium capitalize">{m.tipo.replace(/_/g, ' ')}</span>
                {m.motivo && <span className="text-gray-600"> — {m.motivo}</span>}
              </div>
              <div className="text-right shrink-0">
                <p className="text-gray-500 tabular-nums">{String(m.fecha).slice(0, 10)}</p>
                {m.monto !== null && (
                  <p className="text-gray-700 tabular-nums">{plata(m.monto)}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Activos () {
  const [activos, setActivos] = useState([]);
  const [clases, setClases] = useState([]);
  const [libros, setLibros] = useState([]);
  const [oficial, setOficial] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [estado, setEstado] = useState('');
  const [verId, setVerId] = useState(null);
  const [nuevo, setNuevo] = useState(false);
  const [importar, setImportar] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [a, c, l] = await Promise.all([
        contabilidadApi.getActivos({ estado: estado || undefined }),
        contabilidadApi.getActivoClases(),
        contabilidadApi.getActivoLibros()
      ]);
      setActivos(a.data.activos || []);
      setClases(c.data.clases || []);
      setLibros(l.data.libros || []);
      setOficial(l.data.oficial || null);
    } catch (e) {
      avisar(e, 'No se pudieron cargar los activos');
    } finally {
      setCargando(false);
    }
  }, [estado]);

  useEffect(() => { cargar(); }, [cargar]);

  if (verId) {
    return (
      <Ficha
        id={verId} libros={libros} oficial={oficial}
        onVolver={() => { setVerId(null); cargar(); }}
        onCambio={cargar}
      />
    );
  }

  const enCurso = activos.filter(a => a.estado === 'en_curso').length;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Package size={20} className="text-gray-400" /> Activos fijos
          </h1>
          <p className="text-sm text-gray-500">
            El sub-libro: la ficha, su depreciación por libro y su historia.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setImportar(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
          >
            <Upload size={16} /> Importar
          </button>
          <button
            onClick={() => setNuevo(true)}
            disabled={!clases.length}
            title={clases.length ? '' : 'Primero hace falta una clase: define las cuentas contables'}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Plus size={16} /> Nuevo activo
          </button>
        </div>
      </div>

      {/* Sin clases no hay activos posibles, y decirlo acá evita que el usuario
          llene el formulario para toparse con el error al guardar. */}
      {!cargando && !clases.length && (
        <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 p-4">
          <AlertTriangle className="text-amber-600 mt-0.5 shrink-0" size={20} />
          <p className="text-sm text-amber-900">
            Todavía no hay clases de activo. La clase define las cuentas contables y la
            vida útil, así que hace falta al menos una antes del primer activo.
          </p>
        </div>
      )}

      {enCurso > 0 && (
        <div className="flex items-start gap-3 rounded-xl bg-blue-50 border border-blue-200 p-4">
          <AlertTriangle className="text-blue-600 mt-0.5 shrink-0" size={20} />
          <p className="text-sm text-blue-900">
            {enCurso} activo(s) en curso: no están depreciando. Si ya se instalaron, hay
            que ponerles la fecha de puesta en marcha para que empiecen a generar gasto.
          </p>
        </div>
      )}

      <div className="flex items-center gap-3">
        <label htmlFor="filtro-estado" className="text-sm text-gray-600">Estado</label>
        <select
          id="filtro-estado"
          value={estado}
          onChange={(e) => setEstado(e.target.value)}
          className="border rounded-lg px-3 py-1.5 text-sm"
        >
          <option value="">Todos</option>
          {Object.entries(ESTADOS).map(([k, v]) => (
            <option key={k} value={k}>{v.rotulo}</option>
          ))}
        </select>
      </div>

      {cargando ? <LoadingSpinner /> : (
        <div className="bg-white rounded-xl border overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 border-b">
              <tr>
                <th className="px-5 py-3 text-left font-medium">Código</th>
                <th className="px-5 py-3 text-left font-medium">Nombre</th>
                <th className="px-5 py-3 text-left font-medium">Clase</th>
                <th className="px-5 py-3 text-right font-medium">Valor</th>
                <th className="px-5 py-3 text-left font-medium">En marcha</th>
                <th className="px-5 py-3 text-left font-medium">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {activos.map(a => {
                const est = ESTADOS[a.estado] || { rotulo: a.estado, clase: 'bg-gray-100' };
                const clase = clases.find(c => c.id === a.claseId);
                return (
                  <tr
                    key={a.id}
                    onClick={() => setVerId(a.id)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="px-5 py-3 font-medium">{a.codigo}</td>
                    <td className="px-5 py-3">{a.nombre}</td>
                    <td className="px-5 py-3 text-gray-600">{clase?.codigo || '—'}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{plata(a.valorAdquisicion)}</td>
                    <td className="px-5 py-3 text-gray-600 tabular-nums">
                      {a.fechaPuestaEnMarcha || '—'}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${est.clase}`}>
                        {est.rotulo}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {!activos.length && (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-gray-500">
                    No hay activos{estado ? ' con ese estado' : ''}. Se pueden cargar de a uno
                    o importar el inventario completo, con su depreciación acumulada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {nuevo && (
        <ModalActivo
          clases={clases}
          onCerrar={() => setNuevo(false)}
          onGuardado={() => { setNuevo(false); cargar(); }}
        />
      )}
      {importar && (
        <ImportarActivos
          onCerrar={() => setImportar(false)}
          onImportado={cargar}
        />
      )}
    </div>
  );
}
